import { createSlice } from "@reduxjs/toolkit";
import {
  clearFlightBookingBagStorage,
  persistFlightBookingBag,
} from "../../utils/bookingNonce";

const initialState = {
  // Tags one booking attempt (Search -> Review -> Details -> Seats ->
  // Itinerary). Regenerated on every new search / newly picked flight; `bag`
  // carries whatever nonce was active when it was created (bag.search_nonce).
  // Async handlers compare their captured nonce against this before writing
  // back, so a late response from an abandoned attempt can't overwrite a
  // newer one's TUI (see src/utils/bookingNonce.js).
  activeSearchNonce: null,
  // The full "echoed block" from travel-checklist / seat-layout / itinerary
  // responses: priced_tui, fare_name, seat_layout_available, route_type,
  // fare_summary, NetAmount, SSRAmount, total, total_label,
  // SSR, ssr_addons, ssr_seats, passenger_fares, ssr_addon_lines.
  // Always replaced wholesale, never hand-merged.
  bag: null,
  travellerForm: {
    contact: {
      firstName: "", lastName: "", email: "", phone: "",
      address: "", city: "", state: "", pin: "", countryCode: "IN",
    },
    passengers: [], // [{ paxId, firstName, lastName, gender, dob, nationality, mobile, passportNo, pli, pdoe, pdoi, visaType, panNo }]
  },
  seatSelection: {},
  itinerary: {
    status: "idle", // idle | loading | ready | expired | error
    expiresAt: null,
  },
  booking: {
    bookingId: null,
    gateway: null, // 'razorpay' | 'stripe'
    allowedGateways: null,
    checkout: null,
    paymentLocked: false,
    verifying: false,
  },
  coupon: {
    list: [],
    applied: null,
    validating: false,
  },
  resumeIntent: null, // { returnTo, action: 'createItinerary' }
};

const flightBookingSlice = createSlice({
  name: "flightBooking",
  initialState,
  reducers: {
    setActiveSearchNonce: (state, action) => {
      state.activeSearchNonce = action.payload;
    },
    setBag: (state, action) => {
      state.bag = action.payload;
      persistFlightBookingBag(action.payload);
    },
    setTravellerForm: (state, action) => {
      state.travellerForm = action.payload;
    },
    setSeatSelection: (state, action) => {
      state.seatSelection = action.payload;
    },
    setItineraryStatus: (state, action) => {
      state.itinerary.status = action.payload;
    },
    setItineraryExpiry: (state, action) => {
      state.itinerary.expiresAt = action.payload;
    },
    setBookingId: (state, action) => {
      state.booking.bookingId = action.payload;
    },
    setGateway: (state, action) => {
      state.booking.gateway = action.payload;
    },
    setAllowedGateways: (state, action) => {
      state.booking.allowedGateways = action.payload;
    },
    setCheckout: (state, action) => {
      state.booking.checkout = action.payload;
    },
    setPaymentLocked: (state, action) => {
      state.booking.paymentLocked = action.payload;
    },
    setVerifying: (state, action) => {
      state.booking.verifying = action.payload;
    },
    setCouponList: (state, action) => {
      state.coupon.list = action.payload;
    },
    setAppliedCoupon: (state, action) => {
      state.coupon.applied = action.payload;
    },
    setResumeIntent: (state, action) => {
      state.resumeIntent = action.payload;
    },
    resetFlightBooking: () => {
      clearFlightBookingBagStorage();
      return initialState;
    },
  },
});

export const {
  setActiveSearchNonce,
  setBag,
  setTravellerForm,
  setSeatSelection,
  setItineraryStatus,
  setItineraryExpiry,
  setBookingId,
  setGateway,
  setAllowedGateways,
  setCheckout,
  setPaymentLocked,
  setVerifying,
  setCouponList,
  setAppliedCoupon,
  setResumeIntent,
  resetFlightBooking,
} = flightBookingSlice.actions;

export default flightBookingSlice.reducer;
